<template>
  <!-- Not dismissible, as the classic popup: no close button, Escape is cancelled (reopen) -->
  <dialog ref="el" class="v5-dialog v5-mf" aria-labelledby="v5-mf-h" @cancel.prevent @close="reopen">
    <img v-if="modeArt && !modeArtFailed" class="v5-mf__bg" :src="modeArt" alt="" @error="modeArtFailed = true">
    <div class="v5-mf__in">
      <span v-if="lobby.play_mode?.friendly_name" class="v5-mf__k">
        {{ lobby.play_mode.friendly_name }}<template v-if="teamFormat">, <span class="num">{{ teamFormat }}</span></template>
      </span>
      <h2 id="v5-mf-h">{{ t('v5_ui_shell.match_found') }}</h2>
      <p v-if="lobby.cs_map" class="v5-mf__map">
        <img v-if="!mapPinFailed" :src="`/maps/${lobby.cs_map.name}_pin.png`" alt="" width="20" height="20" @error="mapPinFailed = true">
        <span class="sr">{{ t('v5_ui_shell.map', { map: '' }) }}</span>{{ lobby.cs_map.friendly_name }}
      </p>
      <ul class="v5-mf__faces" :aria-label="t('v5_ui_shell.players')">
        <li v-for="m in members" :key="m.user?.uuid" :class="{ 'is-ok': m.accepted, 'is-you': m.user?.uuid === me }" :title="memberName(m)">
          <img :src="avatarOf(m)" alt="">
          <span class="sr">{{ memberName(m) }}<template v-if="m.accepted">, {{ t('v5_ui_shell.accept') }}</template></span>
        </li>
      </ul>
      <p class="v5-mf__meta">
        <span>{{ t('v5_ui_shell.accepted_n', { n: acceptedCount, m: members.length }) }}</span>
        <span v-if="!allIn" :class="{ 'v5-bad': urgentTimer }">{{ t('v5_ui_shell.left', { time: remainingLabel }) }}</span>
      </p>
      <button v-if="!accepted" class="v5-btn-p v5-mf__go" type="button" :disabled="accepting" @click="accept">
        {{ accepting ? t('v5_ui_shell.accepting') : t('v5_ui_shell.accept') }}
      </button>
      <p v-else class="v5-mf__ok" role="status">
        <CheckIcon class="i" aria-hidden="true" />{{ allIn ? t('v5_ui_shell.everyone_in') : t('v5_ui_shell.accepted_waiting') }}
      </p>
      <div v-if="lanManager" class="v5-mf__lan">
        <p>{{ t('v5_ui_shell.cancel_match_hint') }}</p>
        <button class="v5-btn" type="button" :disabled="cancelling" @click="cancelMatch">{{ t('v5_ui_shell.cancel_match') }}</button>
      </div>
      <p v-if="error" class="v5-mf__err v5-bad" role="alert">{{ error }}</p>
    </div>
  </dialog>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// eslint-disable-next-line import-x/extensions
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { CheckIcon } from '@heroicons/vue/24/outline';
import useLobbyFound from '../../composables/useLobbyFound';

// Match found (mockup matchmaking "Match found"): the classic accept flow (useLobbyFound).
const props = defineProps({
  lobby: { type: Object, required: true },
});
const { t } = useI18n();
const auth = useAuth();
const el = ref(null);
const me = computed(() => auth.user()?.uuid);

const {
  accepting, error, members, teamFormat, modeArt, modeArtFailed, mapPinFailed, acceptedCount,
  allIn, accepted, remainingLabel, urgentTimer, memberName, avatarOf, accept, cancelling,
  lanManager, cancelMatch,
} = useLobbyFound(props);

onMounted(() => el.value?.showModal?.());
// Chromium only lets a page cancel Escape after a user activation; a forced close reopens it
function reopen() { if (el.value?.isConnected) el.value.showModal?.(); }
</script>

<style>
.v5-mf { position: relative; overflow: hidden; }
.v5-mf__bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.12; filter: blur(2px); pointer-events: none; }
.v5-mf__in { position: relative; display: grid; justify-items: center; gap: 12px; padding: 28px 24px 24px; text-align: center; }
.v5-mf__k { color: var(--fg-2); font-size: var(--t-xs); font-weight: 600; }
.v5-mf h2 { margin: -8px 0 0; font-size: 1.75rem; font-weight: 700; }
.v5-mf__map { display: inline-flex; align-items: center; gap: 6px; margin: 0; color: var(--fg-2); font-weight: 600; }
.v5-mf__map img { object-fit: contain; }
.v5-mf__faces { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin: 4px 0; padding: 0; list-style: none; }
.v5-mf__faces li { width: 36px; height: 36px; overflow: hidden; border: 2px solid var(--rule); border-radius: 50%; background: var(--raised); transition: border-color var(--fast) var(--ease); }
.v5-mf__faces li img { width: 100%; height: 100%; object-fit: cover; opacity: 0.45; filter: grayscale(1); }
.v5-mf__faces li.is-ok { border-color: var(--good); }
.v5-mf__faces li.is-ok img { opacity: 1; filter: none; }
.v5-mf__faces li.is-you { outline: 2px dashed var(--muted); outline-offset: 2px; }
.v5-mf__meta { display: flex; gap: 16px; margin: 0; color: var(--fg-2); font-size: var(--t-sm); font-variant-numeric: tabular-nums; }
.v5-mf__go { width: 100%; min-height: 52px !important; font-size: var(--t-lg); }
.v5-mf__ok { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 52px; margin: 0; border-radius: var(--r); background: color-mix(in oklab, var(--good), transparent 85%); color: var(--good); font-weight: 600; }
.v5-mf__ok .i { width: 18px; height: 18px; }
.v5-mf__lan { display: flex; align-items: center; gap: 12px; width: 100%; padding-top: 12px; border-top: 1px solid var(--rule); text-align: left; }
.v5-mf__lan p { flex: 1; margin: 0; color: var(--fg-2); font-size: var(--t-xs); }
.v5-mf__err { margin: 0; font-size: var(--t-sm); }
@media (prefers-reduced-motion: reduce) { .v5-mf__faces li { transition: none; } }
</style>
